import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import NormalHeader from '../components/NormalHeader';
import NormalFooter from '../components/NormalFooter';
import { useNormalReservation } from '../context/NormalReservationContext';
import { clinicApi, slotApi } from '../../services/api';
import { Clinic, Slot } from '../../context/ReservationContext';
import { prefectures } from '../../utils/prefectures';

type SlotMap = Record<string, Slot[]>;

const NormalFormStep1: React.FC = () => {
  const navigate = useNavigate();
  const { prefecture, clinic, setPrefecture, setClinic, setDateTime } = useNormalReservation();
  const [clinics, setClinics] = useState<Clinic[]>([]);
  const [slots, setSlots] = useState<SlotMap>({});
  const [loadingClinics, setLoadingClinics] = useState(false);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadClinics = async (pref: string) => {
    if (!pref) {
      setClinics([]);
      return;
    }
    setLoadingClinics(true);
    setError(null);
    try {
      const data = await clinicApi.getByPrefecture(pref);
      setClinics(data);
    } catch (err) {
      setError('クリニック情報の取得に失敗しました。');
      setClinics([]);
    } finally {
      setLoadingClinics(false);
    }
  };

  const loadSlots = async (clinicId: string) => {
    setLoadingSlots(true);
    setError(null);
    try {
      const data = await slotApi.getByClinic(clinicId);
      setSlots(data);
    } catch (err) {
      setError('予約枠情報の取得に失敗しました。');
      setSlots({});
    } finally {
      setLoadingSlots(false);
    }
  };

  const handlePrefectureChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const pref = event.target.value;
    setPrefecture(pref);
    setClinic(null);
    setSlots({});
    if (pref) {
      loadClinics(pref);
    } else {
      setClinics([]);
    }
  };

  const handleClinicChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selected = clinics.find((c) => c._id === event.target.value);
    if (selected) {
      setClinic(selected);
      loadSlots(selected._id);
    }
  };

  const handleSlotSelect = (date: string, time: string) => {
    setDateTime({ date, time });
    navigate('/form/normal/form2');
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const weekdays = ['日', '月', '火', '水', '木', '金', '土'];
    return `${date.getFullYear()}年${date.getMonth() + 1}月${date.getDate()}日(${weekdays[date.getDay()]})`;
  };

  useEffect(() => {
    if (prefecture && clinics.length === 0) {
      loadClinics(prefecture);
    }
  }, [prefecture, clinics.length]);

  return (
    <div className="flex min-h-screen flex-col bg-gray-50">
      <NormalHeader />
      <main className="mx-auto w-full max-w-5xl flex-1 px-4 py-10 sm:px-6">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-sm font-semibold text-brand-primary">Step 1</p>
            <h1 className="text-2xl font-bold text-gray-900">クリニックと予約日時を選択</h1>
            <p className="text-sm text-gray-600">都道府県 → クリニック → 予約枠の順に選択してください。</p>
          </div>
          <div className="rounded-full bg-white px-4 py-2 text-xs font-semibold text-gray-600 shadow-sm ring-1 ring-gray-100">
            Tailwind Normal Flow
          </div>
        </div>

        <div className="grid gap-6 lg:grid-cols-5">
          <section className="lg:col-span-2 rounded-2xl bg-white p-6 shadow-sm ring-1 ring-gray-100">
            <label className="block text-sm font-semibold text-gray-800">都道府県</label>
            <select
              value={prefecture}
              onChange={handlePrefectureChange}
              className="mt-2 w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm focus:border-brand-primary focus:outline-none focus:ring-1 focus:ring-brand-primary"
            >
              <option value="">- 選択してください -</option>
              {prefectures.map((pref) => (
                <option key={pref} value={pref}>
                  {pref}
                </option>
              ))}
            </select>
            <p className="mt-2 text-xs text-gray-500">お近くの都道府県を選択してください。</p>

            {loadingClinics && <p className="mt-3 text-sm text-gray-600">クリニック情報を取得中...</p>}
            {error && <p className="mt-3 text-sm text-red-600">{error}</p>}

            {prefecture && !loadingClinics && (
              <div className="mt-4 space-y-3">
                <p className="text-sm font-semibold text-gray-800">クリニック</p>
                {clinics.length === 0 ? (
                  <p className="text-sm text-gray-500">該当するクリニックがありません。</p>
                ) : (
                  <div className="space-y-2">
                    {clinics.map((c) => (
                      <label key={c._id} className="flex cursor-pointer items-start gap-3 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 hover:border-brand-primary">
                        <input
                          type="radio"
                          name="clinic"
                          value={c._id}
                          checked={clinic?._id === c._id}
                          onChange={handleClinicChange}
                          className="mt-1"
                        />
                        <div>
                          <p className="text-sm font-semibold text-gray-900">{c.name}</p>
                          <p className="text-xs text-gray-600">{c.address}</p>
                          <p className="text-xs text-gray-600">{c.phone}</p>
                        </div>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            )}
          </section>

          <section className="lg:col-span-3 rounded-2xl bg-white p-6 shadow-sm ring-1 ring-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-gray-800">予約枠</p>
                <p className="text-xs text-gray-500">クリニックを選択すると、予約可能な枠が表示されます。</p>
              </div>
              {loadingSlots && <span className="text-xs text-gray-500">読み込み中...</span>}
            </div>

            {clinic && Object.keys(slots).length > 0 ? (
              <div className="mt-4 space-y-3">
                {Object.keys(slots)
                  .sort()
                  .map((date) => (
                    <div key={date} className="rounded-lg border border-gray-100 bg-gray-50 p-3">
                      <div className="mb-2 flex items-center justify-between">
                        <p className="text-sm font-semibold text-gray-900">{formatDate(date)}</p>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {slots[date].map((slot) => (
                          <button
                            key={`${date}-${slot.time}`}
                            disabled={!slot.available}
                            onClick={() => handleSlotSelect(date, slot.time)}
                            className={`rounded-lg px-3 py-2 text-xs font-semibold shadow-sm ${
                              slot.available
                                ? 'bg-white text-gray-900 ring-1 ring-gray-200 hover:border-brand-primary hover:ring-brand-primary'
                                : 'bg-gray-100 text-gray-400 line-through'
                            }`}
                          >
                            {slot.time}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
              </div>
            ) : (
              <div className="mt-6 rounded-lg border border-dashed border-gray-200 p-6 text-sm text-gray-500">
                予約枠は、クリニックを選択すると表示されます。
              </div>
            )}
          </section>
        </div>
      </main>
      <NormalFooter />
    </div>
  );
};

export default NormalFormStep1;
